import React, { useMemo } from 'react'
import Select from 'react-select'
import { buildPersonOptions, personOptionMatches } from '../../../utils/personSelect'
import { formatImageBytes } from '../../../utils/normalizePersonImage'
import '../../../css/programOverlay.css'
import '../../../css/personSelect.css'

const ProgramEditForm = ({
  editData,
  onChange,
  onFileChange,
  onEpisodeLogoFileChange,
  preview,
  episodeLogoPreview,
  imageInfo,
  episodeLogoInfo,
  isImageLoading,
  isEpisodeLogoLoading,
  isSubmitting,
  error,
  protagonists,
  expectedGuests,
  topics,
  onSave,
  onCancel,
  peopleList,
  topicList,
  addProtagonist,
  removeProtagonist,
  addExpectedGuest,
  removeExpectedGuest,
  addTopic,
  removeTopic,
}) => {
  const personOptions = useMemo(
    () => buildPersonOptions(peopleList),
    [peopleList],
  )

  const availableProtagonists = useMemo(
    () => personOptions.filter(option => !protagonists.includes(option.value)),
    [personOptions, protagonists],
  )

  const availableExpectedGuests = useMemo(
    () => personOptions.filter(option => !expectedGuests.includes(option.value)),
    [personOptions, expectedGuests],
  )

  return (
    <div className="program-edit-form">
      {error && <p className="error">{error}</p>}

      <div className="form-group">
        <label>Titolo:</label>
        <input
          type="text"
          name="title"
          value={editData.title}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label>Descrizione:</label>
        <textarea
          name="description"
          value={editData.description}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label>URL Immagine Corrente:</label>
        <input
          type="text"
          name="imageUrl"
          value={editData.imageUrl || ''}
          readOnly
        />
      </div>

      <div className="form-group">
        <label>Carica Nuova Immagine (base64):</label>
        <input
          type="file"
          name="image"
          onChange={onFileChange}
          accept="image/*"
          disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
        />
        {isImageLoading && <p>Preparazione copertina…</p>}
        {preview && (
          <div className="preview-container">
            <img
              src={preview}
              alt="Anteprima"
              style={{ maxWidth: '300px' }}
            />
          </div>
        )}
        {imageInfo && (
          <small>
            Copertina ottimizzata: {imageInfo.width}×{imageInfo.height}, {' '}
            {formatImageBytes(imageInfo.originalBytes)} → {formatImageBytes(imageInfo.outputBytes)}
          </small>
        )}
      </div>

      <div className="form-group">
        <label>Logo Episodio Corrente:</label>
        {editData.episodeLogoUrl ? (
          <div className="preview-container">
            <img
              src={editData.episodeLogoUrl}
              alt="Logo Episodio"
              style={{ maxWidth: '200px' }}
            />
          </div>
        ) : (
          <p style={{ color: '#888', fontSize: '0.9em' }}>Nessun logo episodio impostato</p>
        )}
      </div>

      <div className="form-group">
        <label>Carica Logo Episodio:</label>
        <input
          type="file"
          name="episodeLogo"
          onChange={onEpisodeLogoFileChange}
          accept="image/*"
          disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
        />
        {isEpisodeLogoLoading && <p>Preparazione logo episodio…</p>}
        {episodeLogoPreview && (
          <div className="preview-container">
            <img
              src={episodeLogoPreview}
              alt="Anteprima Logo Episodio"
              style={{ maxWidth: '200px' }}
            />
          </div>
        )}
        {episodeLogoInfo && (
          <small>
            Logo ottimizzato: {episodeLogoInfo.width}×{episodeLogoInfo.height}, {' '}
            {formatImageBytes(episodeLogoInfo.originalBytes)} → {formatImageBytes(episodeLogoInfo.outputBytes)}
          </small>
        )}
      </div>

      <div className="form-group">
        <label>Protagonisti:</label>
        <div className="chip-container">
          {peopleList
            .filter(person => protagonists.includes(person.id))
            .map(person => (
              <div key={person.id} className="chip">
                <img
                  src={person.imageUrl || 'https://via.placeholder.com/30'}
                  alt={`${person.nome} ${person.cognome}`}
                />
                <span>{person.nome} {person.cognome}</span>
                <button
                  type="button"
                  className="remove-chip"
                  onClick={() => removeProtagonist(person.id)}
                >
                  ×
                </button>
              </div>
          ))}
        </div>
        <Select
          className="person-picker"
          classNamePrefix="person-picker"
          options={availableProtagonists}
          filterOption={personOptionMatches}
          value={null}
          onChange={option => option && addProtagonist(option.value)}
          placeholder="Cerca un protagonista..."
          noOptionsMessage={() => 'Nessuna persona trovata'}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        />
      </div>

      <div className="form-group">
        <label>Expected Guests:</label>
        <div className="chip-container">
          {peopleList
            .filter(person => expectedGuests.includes(person.id))
            .map(person => (
              <div key={person.id} className="chip">
                <img
                  src={person.imageUrl || 'https://via.placeholder.com/30'}
                  alt={`${person.nome} ${person.cognome}`}
                />
                <span>{person.nome} {person.cognome}</span>
                <button
                  type="button"
                  className="remove-chip"
                  onClick={() => removeExpectedGuest(person.id)}
                >
                  ×
                </button>
              </div>
          ))}
        </div>
        <Select
          className="person-picker"
          classNamePrefix="person-picker"
          options={availableExpectedGuests}
          filterOption={personOptionMatches}
          value={null}
          onChange={option => option && addExpectedGuest(option.value)}
          placeholder="Cerca un ospite..."
          noOptionsMessage={() => 'Nessuna persona trovata'}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        />
      </div>

      <div className="form-group">
        <label>Topics:</label>
        <div className="chip-container">
          {topicList
            .filter(topic => topics.includes(topic.id))
            .map(topic => (
              <div key={topic.id} className="chip topic-chip">
                <span>{topic.name}</span>
                <button
                  type="button"
                  className="remove-chip"
                  onClick={() => removeTopic(topic.id)}
                >
                  ×
                </button>
              </div>
          ))}
        </div>
        <select
          onChange={(e) => {
            const val = Number(e.target.value)
            if (val) addTopic(val)
          }}
          value=""
        >
          <option value="" disabled>Seleziona un topic...</option>
          {topicList
            .filter(topic => !topics.includes(topic.id))
            .map(topic => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
          ))}
        </select>
      </div>

      <div className="modal-actions">
        <button
          className="save-edit-btn"
          onClick={onSave}
          disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
        >
          {isImageLoading || isEpisodeLogoLoading
            ? 'Preparazione immagine…'
            : isSubmitting
              ? 'Salvataggio…'
              : 'Salva'}
        </button>
        <button
          className="cancel-edit-btn"
          onClick={onCancel}
          disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
        >
          Annulla
        </button>
      </div>
    </div>
  )
}

export default ProgramEditForm
